import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Grid,
    Paper,
    Typography
} from '@material-ui/core';
import Assignment from '@material-ui/icons/AssignmentOutlined';
import AttachFile from '@material-ui/icons/AttachFileOutlined';
import Edit from '@material-ui/icons/EditOutlined';
import Event from '@material-ui/icons/EventOutlined';
import MonetizationOn from '@material-ui/icons/MonetizationOnOutlined';
import Person from '@material-ui/icons/PersonOutlined';
import Print from '@material-ui/icons/PrintOutlined';
import School from '@material-ui/icons/SchoolOutlined';
import Warning from '@material-ui/icons/WarningOutlined';
import ExitToApp from '@material-ui/icons/ExitToAppOutlined';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { containedButton, outlinedButton } from '../../../utils/styles.js';
import useCreateFile from '../../../hooks/useCreateFile.js';
import useCarregamentoPagina from '../../administrativo/components/useCarregamentoPagina.js';
import useErrorMessage from '../../../hooks/useErrorMessage.js';
import { URL_INSCRICAO_FICHA } from '../../../constants';

import PageTitle from '../../shared/pagetitle/PageTitle';
import PageWrapper from '../../shared/pagewrapper/PageWrapper';
import DataGrid from '../../shared/datagrid/DataGrid.tsx';
import InstructionMessage from '../../shared/instructionmessage/InstructionMessage.js';
import LabelValue from '../../shared/labelvalue/LabelValue.js';
import SheetAcoesInscricao from './inscricao/SheetAcoesInscricao.jsx';
import { avisoPeriodoInscricao } from './inscricao/periodoInscricao';
import useIsMobile from '../../../hooks/useIsMobile';
import { formatCpfMasked, formatDateDisplay, getPendenciasInscricao } from '../../../utils';
import { mensagemDoErro } from '../../../utils/erro';

const useStyles = makeStyles(theme => ({
    ...containedButton(theme, false, false),
    ...outlinedButton(theme, false, false),
    root: {
        paddingBottom: theme.spacing(4)
    },
    actionCard: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
        backgroundColor: theme.palette.grey[50],
        borderLeft: `6px solid ${theme.palette.primary.main}`,
        borderRadius: 4
    },
    actionButtons: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    botaoAbrirAcoes: {
        width: '100%',
        marginBottom: theme.spacing(3)
    },
    cardActions: {
        padding: theme.spacing(0, 2, 2)
    },
    /* A classe outlinedButton do projeto fixa width: initial; aqui o botão ocupa o card. */
    botaoFicha: {
        width: '100%'
    },
    /* Isola o Sair na ponta direita da fileira, longe das ações de edição. */
    botaoSair: {
        marginLeft: 'auto'
    },
    card: {
        height: '100%'
    },
    cardTitle: {
        alignItems: 'center',
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
        /* Ícone do título no padrão do projeto: outline, azul mais claro. */
        '& > svg': {
            color: theme.palette.tertiary.main
        }
    },
    /* Pendência mantém a cor de alerta, casando com a borda do card. */
    iconeAviso: {
        color: `${theme.palette.warning.main} !important`
    },
    field: {
        marginBottom: theme.spacing(1.5)
    },
    pendingCard: {
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        marginTop: theme.spacing(2)
    },
    pendingGrid: {
        marginTop: theme.spacing(2)
    },
    acoesPendencia: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
        /* No celular o botão ocupa a largura do card, como o Gerar Ficha. */
        [theme.breakpoints.down('xs')]: {
            '& > button': {
                width: '100%'
            }
        }
    },
    emptyState: {
        padding: theme.spacing(3),
        textAlign: 'center'
    }
}));

const hasValue = value => value !== null && value !== undefined && String(value).trim() !== '';

const formatPeriod = calendario => {
    if (!calendario) return '';

    const inicio = formatDateDisplay(calendario.dataInicial);
    const fim = formatDateDisplay(calendario.dataFinal);

    if (inicio && fim) return `${inicio} a ${fim}`;
    return inicio || fim;
};

const getPendenciaResponsavel = pendencia =>
    pendencia?.composicaoFamiliar?.descricao
    || pendencia?.composicaoFamiliar?.nome
    || 'Próprio inscrito';

const getPendenciaSituacao = status =>
    String(status || 'A').toUpperCase() === 'I' ? 'Inativa' : 'Ativa';

/* Mesmo par rótulo/valor dos formulários; campo sem valor não aparece. */
const Field = ({ label, value, className }) => {
    if (!hasValue(value)) return null;

    return <LabelValue className={className} label={label} value={String(value)} />;
};

export default function PageInscricaoExistente() {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const identificacao = location.state?.identificacao;
    const [createFile, isGeneratingFicha] = useCreateFile({returnInnerLoadingState: true});
    // Geração da ficha pelo LoadingModal global.
    useCarregamentoPagina(isGeneratingFicha);
    const showErrorMessage = useErrorMessage();
    const isMobile = useIsMobile();
    const [acoesAbertas, setAcoesAbertas] = useState(false);

    useEffect(() => {
        if (!identificacao) {
            navigate('/publico/identificacao');
        }
    }, [identificacao, navigate]);

    const handleExit = () => navigate('/publico/identificacao');
    const handleEditStep = (stepIndex, navigationState = {}) => navigate('/publico/inscricao-nova', {
        state: {
            identificacao,
            isEdit: true,
            initialStep: stepIndex,
            ...navigationState
        }
    });
    const handleDocuments = () => handleEditStep(3, { focusPendingTab: true });

    if (!identificacao) return null;

    const inscricao = identificacao.dadosCadastrais;
    if (!inscricao) {
        return (
            <PageWrapper>
                <Box className={classes.root}>
                    <PageTitle title="Inscrição" />
                    <Paper className={classes.emptyState}>
                        <Typography variant="body1">
                            Não foi possível carregar os dados da inscrição.
                        </Typography>
                        <Button color="primary" onClick={handleExit} variant="contained">
                            Voltar
                        </Button>
                    </Paper>
                </Box>
            </PageWrapper>
        );
    }

    const vaga = inscricao.vaga || {};
    const pessoa = inscricao.pessoa || {};

    const handleGenerateFicha = () => {
        // A inscrição da ficha sai da credencial no back; o corpo vai vazio.
        createFile(URL_INSCRICAO_FICHA, {}, response => {
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const disposition = response.headers?.['content-disposition'];
            const fileNameMatch = disposition && disposition.match(/filename="?([^";]+)"?/i);
            const fileName = fileNameMatch?.[1] || `Ficha_Inscricao_${inscricao.id}.pdf`;

            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
        }, error => {
            showErrorMessage(mensagemDoErro(error, 'Falha ao gerar a ficha de inscrição.'), error?.mensagem || error);
        });
    };
    const pendencias = getPendenciasInscricao(identificacao);
    const periodo = formatPeriod(inscricao.processoSeletivoCalendarioInscricao?.calendario);
    const dataAtualizacao = formatDateDisplay(inscricao.dataAtualizacaoPSInscricao);
    /* Na área pública a situação fica sempre "Em análise", qualquer que seja a gravada. */
    const status = 'Em análise';

    /*
     * Fora do período de inscrição (ou sem calendário de inscrição) as seções
     * abrem só para consulta, e Documentos segue os calendários de entrega,
     * como no JSF. Nulo dentro do período (ou com a flag ausente).
     */
    const avisoPeriodo = avisoPeriodoInscricao(identificacao);

    const fecharAcoes = () => setAcoesAbertas(false);

    /* Seções da inscrição, na mesma ordem das etapas do formulário. */
    const acoesInscricao = [
        {
            id: 'dadosPessoais',
            icone: <Person />,
            titulo: 'Dados pessoais',
            descricao: 'Nome, CPF, endereço',
            onClick: () => handleEditStep(0)
        },
        {
            id: 'socioeconomico',
            icone: <MonetizationOn />,
            titulo: 'Socioeconômico',
            descricao: 'Renda e grupo familiar',
            onClick: () => handleEditStep(1)
        },
        {
            id: 'dadosInscricao',
            icone: <Assignment />,
            titulo: 'Dados da inscrição',
            descricao: 'Curso, cotas, escolaridade',
            onClick: () => handleEditStep(2)
        },
        {
            id: 'documentos',
            icone: <AttachFile />,
            titulo: 'Documentos',
            descricao: pendencias.length > 0
                ? `${pendencias.length} pendência(s) aguardando correção`
                : 'Documentos enviados',
            contador: pendencias.length,
            /* Com pendência, cai direto na aba do integrante pendente. */
            onClick: () => (pendencias.length > 0 ? handleDocuments() : handleEditStep(3))
        }
    ];

    const pendenciaColumns = [
        {
            label: 'Tipo de documento',
            field: 'documento.nome'
        },
        {
            label: 'Descrição da pendência',
            field: 'descricao',
            valueGetter: (value, row) => value || row.documento?.descricao || 'Pendência não informada'
        },
        {
            label: 'Documento para',
            field: 'composicaoFamiliar.descricao',
            valueGetter: (value, row) => getPendenciaResponsavel(row)
        },
        {
            label: 'Situação',
            field: 'status',
            valueGetter: value => getPendenciaSituacao(value)
        }
    ];

    /*
     * No mobile o cartão fica com um botão só e as opções sobem numa folha
     * inferior; no desktop segue a fileira de botões de sempre.
     */
    const blocoAcoes = isMobile ? (
        <Button
            variant="contained"
            className={clsx(classes.containedButton, classes.botaoAbrirAcoes)}
            startIcon={<Edit />}
            onClick={() => setAcoesAbertas(true)}>
            {pendencias.length > 0
                ? `Alterar inscrição (${pendencias.length} pendência(s))`
                : 'Alterar inscrição'}
        </Button>
    ) : (
        <Paper className={classes.actionCard} elevation={3}>
                <>
                    <Typography variant="h6" gutterBottom>
                        Deseja alterar ou visualizar alguma informação da sua inscrição?
                    </Typography>
                    <Typography variant="body2">
                        {avisoPeriodo
                            ? 'Selecione a seção para consultar as informações declaradas.'
                            : 'Selecione a seção correspondente para realizar modificações nas informações declaradas.'}
                    </Typography>
                    <div className={classes.actionButtons}>
                        {/* Mesmas seções (e ícones) da folha do mobile. */}
                        {acoesInscricao.map(acao => (
                            <Button
                                key={acao.id}
                                variant="outlined"
                                className={classes.outlinedButton}
                                startIcon={acao.icone}
                                onClick={acao.onClick}>
                                {acao.titulo}
                            </Button>
                        ))}
                        <Button
                            variant="outlined"
                            className={clsx(classes.outlinedButton, classes.botaoSair)}
                            startIcon={<ExitToApp />}
                            onClick={handleExit}>
                            Sair
                        </Button>
                    </div>
                </>
        </Paper>
    );

    return (
        <PageWrapper>
            <Box className={classes.root}>
                {/* No mobile o Sair fica na linha do título; no desktop, na fileira de ações. */}
                <PageTitle title="Inscrição">
                    {isMobile && (
                        <Button
                            size="small"
                            variant="outlined"
                            className={classes.outlinedButton}
                            startIcon={<ExitToApp />}
                            onClick={handleExit}>
                            Sair
                        </Button>
                    )}
                </PageTitle>

                {avisoPeriodo && (
                    <InstructionMessage
                        title="Atenção!"
                        type="warning"
                        variant="standard"
                        items={[avisoPeriodo]}
                    />
                )}

                {blocoAcoes}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card className={classes.card}>
                            <CardContent>
                                <Box className={classes.cardTitle}>
                                    <School />
                                    <Typography variant="h6">Vaga escolhida</Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item md={6} xs={12}>
                                        <Field className={classes.field} label="Curso" value={vaga.curso?.descricao} />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <Field className={classes.field} label="Turno" value={vaga.turno?.descricao} />
                                    </Grid>
                                    <Grid item md={3} sm={6} xs={12}>
                                        <Field className={classes.field} label="Campus" value={vaga.campus?.nome} />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <Card className={classes.card}>
                            <CardContent>
                                <Box className={classes.cardTitle}>
                                    <Event />
                                    <Typography variant="h6">Resumo da inscrição</Typography>
                                </Box>
                                <Field className={classes.field} label="Situação" value={status} />
                                <Field className={classes.field} label="Número da inscrição" value={inscricao.id} />
                                <Field className={classes.field} label="Última atualização" value={dataAtualizacao} />
                                <Field className={classes.field} label="Período de inscrição" value={periodo} />
                            </CardContent>
                            <CardActions className={classes.cardActions}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    className={clsx(classes.outlinedButton, classes.botaoFicha)}
                                    startIcon={<Print />}
                                    disabled={isGeneratingFicha}
                                    onClick={handleGenerateFicha}>
                                    {isGeneratingFicha ? 'Gerando Ficha...' : 'Gerar Ficha'}
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <Card className={classes.card}>
                            <CardContent>
                                <Box className={classes.cardTitle}>
                                    <Person />
                                    <Typography variant="h6">Dados pessoais</Typography>
                                </Box>
                                <Field className={classes.field} label="Nome" value={pessoa.nome} />
                                <Field className={classes.field} label="CPF" value={formatCpfMasked(pessoa.cpf)} />
                                <Field className={classes.field} label="E-mail" value={pessoa.email} />
                                <Field className={classes.field} label="Telefone" value={pessoa.telefone} />
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card className={classes.card}>
                            <CardContent>
                                <Box className={classes.cardTitle}>
                                    <AttachFile />
                                    <Typography variant="h6">Documentação</Typography>
                                </Box>
                                <Typography variant="body1">
                                    {pendencias.length > 0
                                        ? `${pendencias.length} pendência(s) aguardando correção.`
                                        : 'Nenhuma pendência informada.'}
                                </Typography>
                                {identificacao.documentosExigidos?.length > 0 && (
                                    <Typography color="textSecondary" variant="body2">
                                        Documentos exigidos: {identificacao.documentosExigidos.length}
                                    </Typography>
                                )}
                                {pendencias.length > 0 && (
                                    <Card className={classes.pendingCard} variant="outlined">
                                        <CardContent>
                                            <Box className={classes.cardTitle}>
                                                <Warning className={classes.iconeAviso} />
                                                <Typography variant="subtitle1">
                                                    Existem documentos pendentes
                                                </Typography>
                                            </Box>
                                            <Box className={classes.pendingGrid}>
                                                <DataGrid
                                                    responsive="card"
                                                    tableContainerProps={{
                                                        component: 'div'
                                                    }}
                                                    rows={pendencias}
                                                    columns={pendenciaColumns}
                                                    comparatorKeys={['documento.nome', 'descricao']}
                                                    paginate
                                                    paginateOptions={{
                                                        rowsPerPage: 5,
                                                        startPage: 0,
                                                        resetPageOnRowsChange: true
                                                    }}
                                                    noRecordsText="Nenhuma pendência encontrada."
                                                />
                                            </Box>
                                            <Box className={classes.acoesPendencia}>
                                                <Button
                                                    variant="outlined"
                                                    className={classes.outlinedButton}
                                                    startIcon={<AttachFile />}
                                                    onClick={handleDocuments}>
                                                    Ver pendências
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <SheetAcoesInscricao
                    aberto={acoesAbertas}
                    onFechar={fecharAcoes}
                    onAbrir={() => setAcoesAbertas(true)}
                    descricao={avisoPeriodo || 'Toque na seção que quer revisar.'}
                    acoes={acoesInscricao}
                />
            </Box>
        </PageWrapper>
    );
}
